import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Empty, Spin, Space, Button } from 'antd';
import { MessageOutlined, DownOutlined } from '@ant-design/icons';
import ChatMessage from './ChatMessage';
import type { ChatMessage as ChatMessageType } from '@/services/chat';

export interface ChatConversationProps {
  messages: ChatMessageType[];
  loading?: boolean;
  emptyText?: string;
  autoScroll?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const ChatConversation: React.FC<ChatConversationProps> = ({
  messages = [],
  loading = false,
  emptyText = "Start a conversation by typing a message below",
  autoScroll = true,
  className,
  style,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const lastScrollTop = useRef(0);

  // Track message content changes for streaming detection
  const messageContents = useMemo(() => 
    messages.map(m => ({ id: m.id, content: m.content, isStreaming: m.isStreaming })), 
    [messages]
  );

  // Scroll to bottom function
  const scrollToBottom = useMemo(() => (smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end',
      });
    }
  }, []);

  // Detect if user has manually scrolled up
  useEffect(() => {
    const scrollContainer = containerRef.current?.querySelector('[data-scroll-container]') as HTMLElement;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50; // 50px threshold
      
      // Only set user scrolling if they scrolled up from the bottom
      if (scrollTop < lastScrollTop.current && !isAtBottom) {
        setIsUserScrolling(true);
      } else if (isAtBottom) {
        setIsUserScrolling(false);
      }
      
      lastScrollTop.current = scrollTop;
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll when messages change (new messages or content updates)
  useEffect(() => {
    if (!autoScroll || isUserScrolling) return;

    // Determine if we should use smooth scrolling
    const lastMessage = messages[messages.length - 1];
    const isStreaming = lastMessage?.isStreaming;
    
    // Use smooth scroll for new messages, instant for streaming updates
    const shouldUseSmooth = !isStreaming;
    
    // Small delay to ensure content is rendered
    const delay = isStreaming ? 5 : 50;
    const timeoutId = setTimeout(() => scrollToBottom(shouldUseSmooth), delay);
    
    return () => clearTimeout(timeoutId);
  }, [messageContents, autoScroll, isUserScrolling, scrollToBottom, messages]);

  const containerStyle: React.CSSProperties = {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '16px 0',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative', // For absolute positioned scroll button
    ...style,
  };

  const messagesContainerStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0, // Allow flex child to shrink
  };

  const scrollableAreaStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0 16px',
    // Custom scrollbar styling
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
  };

  if (loading && messages.length === 0) {
    return (
      <div style={containerStyle} className={className}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}>
          <Space direction="vertical" align="center" size={16}>
            <Spin size="large" />
            <span style={{ color: '#8c8c8c' }}>Loading conversation...</span>
          </Space>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div style={containerStyle} className={className}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}>
          <Empty
            image={<MessageOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
            description={
              <span style={{ color: '#8c8c8c', fontSize: 14 }}>
                {emptyText}
              </span>
            }
            imageStyle={{
              height: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      style={containerStyle} 
      className={className}
    >
      <div style={messagesContainerStyle}>
        <div style={scrollableAreaStyle} data-scroll-container>
          {messages.map((message, index) => {
            const isFirstMessage = index === 0;
            const isLastMessage = index === messages.length - 1;
            const prevMessage = messages[index - 1];
            const nextMessage = messages[index + 1];
            
            // Show avatar if it's the first message, last message, or speaker changes
            const showAvatar = 
              isFirstMessage || 
              isLastMessage || 
              !prevMessage || 
              prevMessage.isUser !== message.isUser ||
              !nextMessage ||
              nextMessage.isUser !== message.isUser;

            // Show timestamp if it's the last message or significant time gap
            const showTimestamp = 
              isLastMessage || 
              !nextMessage ||
              nextMessage.isUser !== message.isUser ||
              (nextMessage.timestamp.getTime() - message.timestamp.getTime()) > 300000; // 5 minutes

            return (
              <ChatMessage
                key={message.id}
                message={message}
                showAvatar={showAvatar}
                showTimestamp={showTimestamp}
              />
            );
          })}
          
          {/* Invisible element to scroll to */}
          <div ref={messagesEndRef} style={{ height: 1 }} />
        </div>
      </div>
      
      {/* Scroll to bottom button when user has scrolled up */}
      {isUserScrolling && (
        <div style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          zIndex: 10,
        }}>
          <Button
            type="primary"
            shape="circle"
            icon={<DownOutlined />}
            size="small"
            onClick={() => {
              setIsUserScrolling(false);
              scrollToBottom(true);
            }}
            style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ChatConversation;
